import { FormField, Select, useToast } from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { formErrors } from '../../../lib/errors';
import { useChangeReferrer, useReferrers, type Referrer, type Sale } from '../api';

/** "Maja Biene", or "Maja Biene (ehemalig)" for former members (#26). */
export function useReferrerName() {
  const { t } = useTranslation();
  return (r: Referrer) => (r.former ? t('sales.referrer.former', { name: r.name }) : r.name);
}

export interface ReferrerFieldProps {
  value: string;
  onChange: (id: string) => void;
  /** The current referrer, kept as option even if they are a former member. */
  current?: Referrer | undefined;
  error?: string | undefined;
  enabled?: boolean;
}

/** "Vermittelt von": one of the current members, required (#26). */
export function ReferrerField({
  value,
  onChange,
  current,
  error,
  enabled = true,
}: ReferrerFieldProps) {
  const { t } = useTranslation();
  const members = useReferrers(enabled);
  const name = useReferrerName();
  const options = (members.data ?? []).map((m) => ({ value: m.id, label: m.name }));
  if (current && !options.some((o) => o.value === current.id)) {
    options.push({ value: current.id, label: name(current) });
  }
  return (
    <FormField label={t('sales.form.referredBy')} required error={error}>
      {(p) => (
        <Select
          {...p}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          options={options.length > 0 ? options : [{ value, label: '…' }]}
        />
      )}
    </FormField>
  );
}

/** Changes "Vermittelt von" of a sale, also after delivery (#26). */
export function ReferrerDialog({
  sale,
  onClose,
}: {
  sale: Pick<Sale, 'id' | 'referred_by'>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const change = useChangeReferrer();
  const [member, setMember] = useState(sale.referred_by.id);
  const errors = formErrors(change.error, { member: ['sale.referrer'] });
  return (
    <FormDialog
      open
      onClose={onClose}
      size="sm"
      title={t('sales.referrer.title')}
      submitLabel={t('common.save')}
      pending={change.isPending}
      error={errors.general}
      onSubmit={() =>
        change.mutate(
          { id: sale.id, member },
          {
            onSuccess: () => {
              toast.show(t('sales.referrer.saved'));
              onClose();
            },
          },
        )
      }
    >
      <ReferrerField
        value={member}
        onChange={setMember}
        current={sale.referred_by}
        error={errors.field.member}
      />
    </FormDialog>
  );
}
